import React, { useState, useEffect, useContext, useRef } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { Box, Button, MenuItem } from '@mui/material';
import { fetchCountryData, fetchCustomerType, fetch360PayCustomerStatuses } from '../../api/util';
import { getAccountDetails, updateAccountDetailsApi } from '../../api/settings';
import Checkbox from '@mui/material/Checkbox';
import { StoreContext } from '../../components/Store';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import LoadingSpinner from '../../components/LoadingSpinner';
import { dateFormatChange } from '../../utils/date';
import { isValidEmail, isValidPhone } from '../../utils/validation';
import PropTypes from 'prop-types';
import FormField from '../../components/FormField';
import { isEORIUser, is360PayStaff } from '../../utils/roles';
import ConfirmModal from './ConfirmModal';

const RenderField = ({ children, select, value, name, readOnly, onChange, label = 'Field', error = '' }) => {
  if (readOnly) return <div className="readonly-field-component">{value || 'n/a'}</div>;

  const helperText = error.replace('$fieldName', label);

  return (
    <TextField
      select={select}
      className="field-component"
      value={value || ''}
      onChange={onChange}
      name={name}
      error={!!error}
      helperText={helperText}
    >
      {children}
    </TextField>
  );
};

RenderField.propTypes = {
  children: PropTypes.node,
  select: PropTypes.bool,
  value: PropTypes.string,
  name: PropTypes.string,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
};

export default function AccountDetails({ companyId }) {
  const store = useContext(StoreContext);
  const [accountsData, setAccountsData] = useState({
    billable: false,
  });
  const isReadOnly = sessionStorage.getItem('department') === 'Haulier' || is360PayStaff();
  const isOpsUser = isEORIUser();

  const [countriesData, setCountriesData] = useState([]);
  const [customerTypeData, setCustomerTypeData] = useState([]);
  const [customerStatusData, setCustomerStatusData] = useState([]);

  const [image, setImage] = useState('');
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleCloseError = () => setErrorOpen(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [isNewActivation, setIsNewActivation] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [is360PayCustomer, setIs360PayCustomer] = useState(false);
  const [is360PaySubscription, setIs360PaySubscription] = useState(false);

  // state for customer type change warning modal
  const [isCustomerTypeWarningOpen, setIsCustomerTypeWarningOpen] = useState(false);
  const [customerTypeWarningMessage, setCustomerTypeWarningMessage] = useState('');
  const [customerTypeChangeConfirmed, setCustomerTypeChangeConfirmed] = useState(null);

  useEffect(() => {
    setIs360PayCustomer(accountsData.customerTypeId === 30);
    setIs360PaySubscription(accountsData.customerTypeId === 60);
  }, [accountsData.customerTypeId]);

  const handleOnChange = (event) => {
    let title = event.target.name;
    let value = event.target.value;

    if (['billable', 'isActivated'].includes(event.target.name)) {
      setAccountsData((prevData) => ({
        ...prevData,
        [title]: event.target.checked,
      }));
    } else {
      setAccountsData((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  const handleCustomerTypeChange = (event) => {
    let warningMessage = '';

    // show warning if changing from certain customer types to others
    if ([30, 60].includes(accountsData.customerTypeId) && [10, 20].includes(parseInt(event.target.value))) {
      warningMessage = 'It is not advised to change a 360PAY customer Type to non 360PAY customer Type.';
    } else if ([10, 20].includes(accountsData.customerTypeId) && [30, 60].includes(parseInt(event.target.value))) {
      warningMessage = 'It is not advised to change a non 360PAY customer Type to a 360PAY customer Type.';
    } else if ([40, 50].includes(accountsData.customerTypeId) && [30, 60].includes(parseInt(event.target.value))) {
      warningMessage = 'You are about to change this to a 360PAY customer who uses SSO.';
    } else if ([40, 50].includes(accountsData.customerTypeId) && [10, 20].includes(parseInt(event.target.value))) {
      warningMessage = 'You are about to change this to a non 360PAY customer.';
    }

    if (warningMessage) {
      setCustomerTypeWarningMessage(warningMessage);
      setIsCustomerTypeWarningOpen(true);
      setCustomerTypeChangeConfirmed(event.target.value);
    } else {
      handleOnChange(event);
    }
  };

  // handle trimming of data when fields lose focus
  const handleOnBlur = (e) => {
    const { name, value } = e.target;
    if (typeof value === 'string') {
      setAccountsData((prevData) => ({
        ...prevData,
        [name]: value.trim(),
      }));
    }
  };

  const processCountryCode = (countryList, value) => {
    const usersCurrentCountry = countryList.find(({ countryCode }) => countryCode === value);
    if (usersCurrentCountry) {
      setAccountsData((prevData) => ({
        ...prevData,
        countryId: usersCurrentCountry['id'],
      }));
    }
  };

  const processLogo = (data) => {
    let binary = '';
    if (data?.logo?.data) {
      const bytes = new Uint8Array(data.logo?.data);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      setAccountsData((prevData) => ({
        ...prevData,
        image: `data:image/png;base64,${btoa(binary)}`,
      }));
    }
  };

  const fetchData = async () => {
    try {
      const countryList = await fetchCountryData(0);
      const customerTypeList = await fetchCustomerType();
      const customerStatusList = await fetch360PayCustomerStatuses();

      setCustomerTypeData(customerTypeList);
      setCustomerStatusData(customerStatusList);
      setCountriesData(countryList);
      getAccountDetails(companyId || store.eoriCompany[0] || sessionStorage.getItem('companyid'))
        .then((data) => {
          if (data) {
            store.accountDetails[1](data);
            for (const [key, value] of Object.entries(data)) {
              if (key === 'contactName') {
                sessionStorage.setItem('contactName', value);
              }
              // finding the ID of the country fetched from the DB and storing this with the account data
              if (key === 'countryCode') {
                processCountryCode(countryList, value);
              }
              if (key === 'logo') {
                processLogo(data);
              }
              if (key === 'isActivated') {
                setIsNewActivation(!value);
              }
              setAccountsData((prevData) => ({
                ...prevData,
                [key]: value,
              }));
            }
          }
        })
        .catch((err) => {
          store.showError(err.message);
        })
        .finally(() => setLoading(false));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(store.accountDetails?.[0]).length > 0) {
      for (const [key, value] of Object.entries(store.accountDetails[0])) {
        setAccountsData((prevData) => ({
          ...prevData,
          [key]: value,
        }));
      }
    }
  }, [store.accountDetails?.[0]]);

  useEffect(() => {
    const customerIs360pay = accountsData.customerTypeId === 30;
    setIs360PayCustomer(customerIs360pay);

    if (customerIs360pay) {
      setAccountsData((prevData) => ({
        ...prevData,
        paymentContactEmail: 'N/A',
        paymentContactPhone: 'N/A',
      }));
    }
  }, [accountsData]);

  const updateAccountDetails = async () => {
    try {
      const errors = {};

      const data = {
        companyId: accountsData.companyId || companyId,
        companyName: accountsData.company,
        streetAddress: accountsData.streetAddress,
        townCity: accountsData.townOrCity,
        countryId: accountsData.countryId,
        postCode: accountsData.postcode,
        logo: image,
        contactName: accountsData.contactName,
        contactEmail: accountsData.contactEmail,
        notifications: accountsData.notifications || '',
        contactPhone: accountsData.contactPhone,
        customerReference: accountsData.customerReference,
        declarant: 1, // Always set to 1 (EORI IRELAND LIMITED) - locked field
        isActivated: accountsData.isActivated ? '1' : '0',
        customerTypeId: accountsData.customerTypeId,
        paymentContactName: accountsData.paymentContactName,
        paymentContactEmail: accountsData.paymentContactEmail,
        paymentContactPhone: accountsData.paymentContactPhone,
        customerStatus360PayId: accountsData.customerStatus360PayId || 0,
      };

      const fieldValidation = {
        companyName: ['required'],
        streetAddress: ['required'],
        townCity: ['required'],
        postCode: ['required'],
        countryId: ['required'],
        contactName: ['required'],
        contactEmail: ['required', 'validEmail'],
        notifications: ['validMultipleEmails'],
        contactPhone: ['required', 'validPhone'],
        customerTypeId: ['required'],
        paymentContactName: ['required'],
        paymentContactEmail: ['required', 'validEmailOrNA'],
        paymentContactPhone: ['required', 'validPhoneOrNA'],
        ...((is360PayCustomer || is360PaySubscription) && {
          customerStatus360PayId: ['required'],
        }),
      };

      Object.keys(data).forEach((field) => {
        const validations = fieldValidation[field] || [];

        validations.forEach((validation) => {
          if (validation === 'required' && (data[field] === '' || !data[field])) {
            errors[field] = '$fieldName is required.';
          } else if (validation === 'validEmail' && !isValidEmail(data[field])) {
            errors[field] = '$fieldName must be a valid email address.';
          } else if (
            validation === 'validEmailOrNA' &&
            !isValidEmail(data[field]) &&
            !(data[field] === 'N/A' && is360PayCustomer)
          ) {
            errors[field] = '$fieldName must be a valid email address';
          } else if (validation === 'validPhone' && !isValidPhone(data[field])) {
            errors[field] = '$fieldName must be a valid phone number.';
          } else if (
            validation === 'validPhoneOrNA' &&
            !isValidPhone(data[field]) &&
            !(data[field] === 'N/A' && is360PayCustomer)
          ) {
            errors[field] = '$fieldName must be a valid phone number.';
          } else if (validation === 'validMultipleEmails' && data[field]) {
            const emails = data[field].split(',').map((email) => email.trim());
            const invalidEmails = emails.filter((email) => email && !isValidEmail(email));
            if (invalidEmails.length > 0) {
              errors[field] = 'Please enter valid email addresses separated by commas.';
            }
          }
        });
      });

      setFormErrors(errors);

      if (!Object.keys(errors).length) {
        const response = await updateAccountDetailsApi(data);

        if (response?.message && response?.isValid) {
          setOpen(true);
          fetchData();
        } else if (response?.error) {
          setErrorOpen(true);
          setErrorMessage(response.error);
        }
      }
    } catch (error) {
      console.error('Error updating account details:', error);
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0'); // 26
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 02 (Months are 0-based)
    const year = String(date.getFullYear()).slice(-2); // 25
    const hours = String(date.getHours()).padStart(2, '0'); // 09
    const minutes = String(date.getMinutes()).padStart(2, '0'); // 51
    const seconds = String(date.getSeconds()).padStart(2, '0'); // 25

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  };
  const onImageChange = (event) => {
    if (event.target.files?.[0]) {
      let img = event.target.files[0];
      setImage(img);
      setAccountsData((prevData) => ({
        ...prevData,
        ['image']: URL.createObjectURL(img),
      }));
    }
  };

  const deleteLogo = () => {
    setAccountsData((prevData) => ({
      ...prevData,
      ['image']: '',
    }));
    setImage('');
    inputRef.current.value = '';
  };

  const getOptionString = (data, idField, nameField, id) => {
    const item = data.find((option) => option[idField] === id);
    return item ? item[nameField] : 'n/a';
  };

  const renderPaymentInformation = () => {
    return (
      <>
        <FormField
          label="Payment Contact Name"
          name="paymentContactName"
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          value={accountsData.paymentContactName || ''}
          readOnly={isReadOnly}
          error={formErrors.paymentContactName}
          required
        />

        <FormField
          label="Payment Contact Email"
          name="paymentContactEmail"
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          value={is360PayCustomer ? 'Managed By 360PAY' : accountsData.paymentContactEmail || ''}
          readOnly={is360PayCustomer || isReadOnly}
          error={formErrors.paymentContactEmail}
          required
        />

        <FormField
          label="Payment Contact Phone"
          name="paymentContactPhone"
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          value={is360PayCustomer ? 'Managed By 360PAY' : accountsData.paymentContactPhone || ''}
          readOnly={is360PayCustomer || isReadOnly}
          error={formErrors.paymentContactPhone}
          required
        />
      </>
    );
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box className="account-settings">
          {isReadOnly && (
            <p>
              If your account settings need to be updated, please contact us using the following{' '}
              <a
                href="https://share.hsforms.com/195eHKT2fQOGQD84W2gSmUg4rhvg"
                target="_blank"
                rel="noopener noreferrer"
              >
                form
              </a>
              .
            </p>
          )}
          <FormField
            label="Company Name"
            name="company"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.company || ''}
            readOnly={isReadOnly}
            error={formErrors.companyName}
            required
          />
          <Box className="field">
            {/* Label */}
            <Typography variant="body1" className="field-label">
              EORI Number
            </Typography>
            {/* Input Field */}
            <div className="readonly-field-component">{accountsData.eori || 'n/a'}</div>
          </Box>

          <Box className="field">
            <Typography variant="body1" className="field-label">
              VAT Number
            </Typography>
            <div className="readonly-field-component">{accountsData.vatNumber || ''}</div>
          </Box>

          <FormField
            label="Street Address"
            name="streetAddress"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.streetAddress || ''}
            readOnly={isReadOnly}
            error={formErrors.streetAddress}
            required
          />

          <FormField
            label="Town / City"
            name="townOrCity"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.townOrCity || ''}
            readOnly={isReadOnly}
            error={formErrors.townCity}
            required
          />

          <FormField
            label="Postcode"
            name="postcode"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.postcode || ''}
            readOnly={isReadOnly}
            error={formErrors.postCode}
            required
          />

          <FormField
            label="Country"
            name="countryId"
            onChange={handleOnChange}
            value={
              isReadOnly
                ? getOptionString(countriesData, 'id', 'countryName', accountsData.countryId)
                : accountsData.countryId
            }
            readOnly={isReadOnly}
            error={formErrors.countryId}
            required
            select
          >
            {countriesData.map((country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.countryName}
              </MenuItem>
            ))}
          </FormField>

          <FormField
            label="Contact Name"
            name="contactName"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.contactName || ''}
            readOnly={isReadOnly}
            error={formErrors.contactName}
            required
          />

          <FormField
            label="Contact Email"
            name="contactEmail"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.contactEmail || ''}
            readOnly={isReadOnly}
            error={formErrors.contactEmail}
            required
          />

          <FormField
            label="Notifications"
            name="notifications"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.notifications || ''}
            readOnly={is360PayStaff()}
            error={formErrors.notifications}
            tooltip="Enter one or more email addresses, separated by commas. These addresses will receive the responses from the declaration."
          />

          <FormField
            label="Contact Phone Number"
            name="contactPhone"
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            value={accountsData.contactPhone || ''}
            readOnly={isReadOnly}
            error={formErrors.contactPhone}
            required
          />

          {renderPaymentInformation()}

          {isOpsUser && (
            <>
              {(is360PayCustomer || is360PaySubscription) && (
                <>
                  <FormField
                    label="Customer Reference"
                    name="customerReference"
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                    value={accountsData.customerReference || ''}
                    readOnly={isReadOnly}
                    error={formErrors.customerReference}
                  />
                  <FormField
                    label="360PAY Customer Status"
                    name="customerStatus360PayId"
                    onChange={handleOnChange}
                    value={
                      isReadOnly
                        ? getOptionString(
                            customerStatusData,
                            'statusId',
                            'statusName',
                            accountsData.customerStatus360PayId
                          )
                        : accountsData.customerStatus360PayId
                    }
                    readOnly={isReadOnly}
                    error={formErrors.customerStatus360PayId}
                    required
                    select
                  >
                    {customerStatusData.map((cs) => (
                      <MenuItem key={cs.statusId} value={cs.statusId}>
                        {cs.statusName}
                      </MenuItem>
                    ))}
                  </FormField>
                </>
              )}
              <Box className="field">
                {/* Label */}
                <Typography variant="body1" className="field-label">
                  Account Activation
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box>
                    <Checkbox
                      className="field-component"
                      onChange={handleOnChange}
                      name="isActivated"
                      checked={accountsData.isActivated}
                      disabled={accountsData.isActivated && !isNewActivation}
                    />
                  </Box>

                  <Box>
                    {accountsData.restrictedDate && (
                      <>
                        last updated by {accountsData.restrictedBy || 'Unknown'} on{' '}
                        {dateFormatChange(accountsData.restrictedDate)}
                      </>
                    )}
                    {accountsData.isActivated && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Account activated - cannot be modified
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              <FormField label="Declarant" name="declarant" value="EORI IRELAND LIMITED" readOnly={true} required />

              <FormField
                label="Customer Type"
                name="customerTypeId"
                onChange={handleCustomerTypeChange}
                value={
                  isReadOnly
                    ? getOptionString(customerTypeData, 'typeId', 'typeName', accountsData.customerTypeId)
                    : accountsData.customerTypeId
                }
                readOnly={isReadOnly}
                error={formErrors.customerTypeId}
                required
                select
              >
                {customerTypeData.map((ct) => (
                  <MenuItem key={ct.typeId} value={ct.typeId}>
                    {ct.typeName}
                  </MenuItem>
                ))}
              </FormField>

              <Box className="field">
                <Typography variant="body1" className="field-label">
                  Logo
                </Typography>
                <div className="logo-upload-container">
                  <div className="logo-upload-input-container">
                    <input
                      ref={inputRef}
                      id="files"
                      type="file"
                      name="myImage"
                      className="logo-upload-input"
                      onChange={onImageChange}
                    />
                    {accountsData.image && <img alt="account data" src={accountsData.image} />}
                  </div>
                  <label htmlFor="files" className="logo-upload-choose-link" style={{ display: 'inline-block' }}>
                    Choose File
                  </label>
                  {accountsData.image && (
                    <>
                      {' '}
                      &nbsp;|&nbsp;{' '}
                      <button onClick={deleteLogo} className="logo-upload-delete-link">
                        Delete logo
                      </button>
                    </>
                  )}
                </div>
              </Box>
            </>
          )}

          {!is360PayStaff() && (
            <Button
              style={{ marginTop: '1rem', marginBottom: '1rem' }}
              className={'button-class'}
              onClick={updateAccountDetails}
            >
              Update Details
            </Button>
          )}
          {companyId && isOpsUser && (
            <>
              <Box className="field">
                <Typography variant="body1" className="field-label">
                  Last Modified By User
                </Typography>
                <div className="readonly-field-component">
                  {accountsData.lastModifiedUser || 'n/a'} -- {accountsData.lastModified || 'n/a'}
                </div>
              </Box>
            </>
          )}

          <Dialog
            open={open}
            onClose={handleClose}
            className="settings-dialog"
            PaperProps={{
              component: 'form',
              onSubmit: (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                const email = formJson.email;
                handleClose();
              },
            }}
          >
            <DialogContent>
              <DialogContentText>
                {accountsData.isActivated && isNewActivation
                  ? 'Account Activation enabled. Email notification sent.'
                  : 'Account details updated successfully'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} className="button-class">
                Ok
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={errorOpen}
            onClose={handleCloseError}
            className="settings-dialog"
            PaperProps={{
              component: 'form',
              onSubmit: (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                const email = formJson.email;
                handleCloseError();
              },
            }}
          >
            <DialogContent>
              <DialogContentText>{errorMessage}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseError} className="button-class">
                Ok
              </Button>
            </DialogActions>
          </Dialog>

          <ConfirmModal
            isOpen={isCustomerTypeWarningOpen}
            heading="Changing Customer Type"
            onCancel={() => setIsCustomerTypeWarningOpen(false)}
            onConfirm={() => {
              handleOnChange({ target: { name: 'customerTypeId', value: customerTypeChangeConfirmed } });
              setIsCustomerTypeWarningOpen(false);
            }}
          >
            <p style={{ fontWeight: 'bold' }}>{customerTypeWarningMessage}</p>
            <p>Please confirm you want to continue and save changes?</p>
          </ConfirmModal>
        </Box>
      )}
    </>
  );
}

AccountDetails.propTypes = {
  companyId: PropTypes.string,
};
